import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getServicePath } from "../../constants/automapPartnershipConstants";
import { useClientViewSeo } from "../../clientViewRuntime/hooks/useClientViewSeo";
import SeoBreadcrumbs from "../../components/automap/seo/SeoBreadcrumbs";
import SeoFaqSection from "../../components/automap/seo/SeoFaqSection";
import { SEO_PAGE_TYPES } from "../../constants/seoPageConstants";
import { trackSeoPageEvent, SEO_ANALYTICS_EVENTS } from "../../constants/seoAnalyticsConstants";
import {
  buildSeoBreadcrumbs,
  buildSeoPageMetadata,
  buildSeoStructuredData,
  buildSearchResultsHref,
  getSeoFaqItems,
  getSeoPageConfig,
} from "../../services/seoPageService";
import "../../styles/seoLanding.css";

export default function SeoGuidePage() {
  const { guideSlug } = useParams();
  const path = `/guias/${guideSlug}`;
  const config = getSeoPageConfig(path);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const metadata = useMemo(
    () => (config ? buildSeoPageMetadata(config, { origin }) : null),
    [config, origin]
  );
  const breadcrumbs = useMemo(() => (config ? buildSeoBreadcrumbs(config) : []), [config]);
  const faqItems = useMemo(() => (config ? getSeoFaqItems(config) : []), [config]);
  const guideContent = config?.guideContent;
  const guideServiceLink = useMemo(() => {
    const map = {
      "compra-segura-carro-usado": null,
      "checklist-compra-carro-usado": "/checklist-compra",
      "soat-tecnomecanica-carro-usado": getServicePath("soat"),
      "financiacion-carro-usado": getServicePath("financing"),
    };
    return map[guideSlug] || "/servicios";
  }, [guideSlug]);

  const structuredData = useMemo(() => {
    if (!config) return null;
    return buildSeoStructuredData(config, { breadcrumbs });
  }, [config, breadcrumbs]);

  useClientViewSeo({
    title: metadata?.metaTitle || "Guía | AutoMapa",
    description: metadata?.metaDescription,
    canonicalUrl: metadata?.canonicalUrl,
    robots: metadata?.robots,
    openGraph: metadata?.openGraph,
    twitter: metadata?.twitter,
    structuredData,
    jsonLdId: "automap-seo-guide-jsonld",
  });

  useEffect(() => {
    if (config?.pageType === SEO_PAGE_TYPES.GUIDE) {
    trackSeoPageEvent(SEO_ANALYTICS_EVENTS.CONTENT_PAGE_VIEWED, { path, pageType: "guide" });
    }
  }, [config, path]);

  if (!config || config.pageType !== SEO_PAGE_TYPES.GUIDE) {
    return (
      <main className="seo-guide seo-guide--not-found">
        <h1>Guía no encontrada</h1>
        <p>Esta guía aún no está disponible en AutoMapa.</p>
        <Link to="/checklist-compra">Ver hub de compra</Link>
      </main>
    );
  }

  const sections = guideContent?.sections || [];
  const relatedGuides = guideContent?.relatedGuides || [];
  const inventoryHref = buildSearchResultsHref(config);

  return (
    <main className="seo-guide">
      <header className="seo-guide__header">
        <div className="seo-landing__brand">
          <Link to="/v/automap">AutoMapa</Link>
        </div>
        <SeoBreadcrumbs items={breadcrumbs} />
        <h1>{config.h1}</h1>
        {config.introCopy ? <p className="seo-guide__intro">{config.introCopy}</p> : null}
      </header>

      {sections.length ? (
        <nav className="seo-guide__toc" aria-label="Contenido">
          <h2>Contenido</h2>
          <ol>
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <div className="seo-guide__sections">
        {sections.map((section) => (
          <section key={section.id} id={section.id}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
          </section>
        ))}
      </div>

      {guideContent?.disclaimer ? (
        <p className="seo-guide__disclaimer">{guideContent.disclaimer}</p>
      ) : null}

      <SeoFaqSection items={faqItems} />

      {relatedGuides.length ? (
        <section className="seo-guide__related">
          <h2>Guías relacionadas</h2>
          <ul>
            {relatedGuides.map((guide) => (
              <li key={guide.href}>
                <Link
                  to={guide.href}
                  onClick={() =>
                    trackSeoPageEvent(SEO_ANALYTICS_EVENTS.GUIDE_CLICKED, { href: guide.href })
                  }
                >
                  {guide.label}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="seo-landing__ctas">
        <Link
          to={inventoryHref}
          className="seo-btn seo-btn--primary"
          onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.FILTER_CLICKED, { source: "guide_inventory" })}
        >
          Ver inventario relacionado
        </Link>
        <Link
          to="/v/automap/comprar/quiz"
          className="seo-btn seo-btn--secondary"
          onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.BUYER_QUIZ_CLICKED, { source: "guide" })}
        >
          Ayúdame a escoger
        </Link>
        <Link
          to="/guias/checklist-compra-carro-usado"
          className="seo-btn seo-btn--ghost"
          onClick={() => trackSeoPageEvent(SEO_ANALYTICS_EVENTS.CHECKLIST_CLICKED, { source: "guide" })}
        >
          Checklist de compra
        </Link>
        <Link to={guideServiceLink} className="seo-btn seo-btn--ghost">
          Solicitar información de servicios
        </Link>
      </section>
    </main>
  );
}
